import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectBlocks } from '@/store/selectors/resultSelectors';
import ConditionalBlock from '@/components/features/result/ConditionalBlock/ConditionalBlock';
import { ResultRecommendationsTypes, RecommendItem, LitresItem, TextItem } from '@/store/types/resultTypes';
import { ResultH2 } from '@/pages/result/styles';
import StyledArticle from '@/components/templates/StyledArticle/StyledArticle';
import Grid from '@/components/molecules/Grid';
import { useTranslation } from 'react-i18next';
import { sanitizeHTML } from '@/core/hooks/useSanitizedHTML';
import styled from 'styled-components';
import AdfoxWidget from '@/components/features/adv/AdfoxWidget/AdfoxWidget';

export function parseAdfoxContent(content: string): {
	ownerId: number;
	containerId: string;
	params: any;
} | null {
	// Ищем вызов Ya.adfoxCode.create({...})
	const regex = /Ya\.adfoxCode\.create\(\s*(\{[\s\S]*?\})\s*\)/;
	const match = content.match(regex);
	if (!match) {
		return null;
	}

	const objectString = match[1];

	// Преобразуем строку объекта в корректный JSON:
	// Добавляем двойные кавычки вокруг имен свойств и заменяем одинарные на двойные
	let jsonString = objectString
	.replace(/([{,]\s*)([a-zA-Z0-9_]+)\s*:/g, '$1"$2":')
	.replace(/'/g, '"');

	try {
		const obj = JSON.parse(jsonString);
		return {
			ownerId: obj.ownerId,
			containerId: obj.containerId,
			params: obj.params
		};
	} catch (e) {
		console.error("Ошибка при разборе JSON:", e);
		return null;
	}
}

// Стили для элемента списка (если нужно)
const RecommendationItem = styled.li``;


const ResultFooterRecommendations: React.FC<{ recommend_for_you: ResultRecommendationsTypes }> = ({ recommend_for_you }) => {

	const blocks = useSelector(selectBlocks);

	const {
		resultRecommendBooks,
		resultRecommendText,
		resultRecommendForYou} = blocks;

	const { t } = useTranslation();
	const text_recommendations = t('result.your_recommendations' as string);
	const text_adv_info = t('result.adv_info' as string);
	const text_litres_read = t('result.litres_read' as string);

	const renderRecommends = (recommends: RecommendItem[]) => (
		<>
			{recommends.map((recommendation, index) => {
				// Если в контенте есть тег <script>, пытаемся распарсить параметры Adfox
				if (/<script[\s\S]*?>[\s\S]*?<\/script>/i.test(recommendation.content)) {
					const adfoxParams = parseAdfoxContent(recommendation.content);
					if (adfoxParams) {
						// Если удалось распарсить параметры, рендерим AdfoxWidget
						return (
							<RecommendationItem key={index} className={recommendation.css_class}>
								<AdfoxWidget
									id={adfoxParams.containerId}
									ownerId={adfoxParams.ownerId}
									containerId={adfoxParams.containerId}
									params={adfoxParams.params}
								/>
							</RecommendationItem>
						);
					}
				}
				// Если скрипта нет или парсинг не удался, рендерим обычный HTML
				return (
					<RecommendationItem
						key={index}
						className={recommendation.css_class}
						dangerouslySetInnerHTML={{ __html: sanitizeHTML(recommendation.content) }}
					/>
				);
			})}
		</>
	);

	// todo нужен перевод слова "Почитать"
	const renderLitres = (litres: LitresItem[]) => (
		<>
			{litres.map((book, index) => (
				<li key={index}>
					{text_litres_read}: <a href={book.link+'?utm_source=advcake&utm_medium=cpa&utm_campaign=affiliate&utm_content=cbbf85df&advcake_params=&utm_term=&erid=2VfnxyNkZrY&advcake_method=1&m=1'} target="_blank" rel="noreferrer noopener">{book.name}</a>. {book.author} ({book.year}).
				</li>
			))}
		</>
	);

	const renderText = (text: TextItem[]) => (
		<>
			{text.map((recommendation, index) => (
				<li
					key={index}
					dangerouslySetInnerHTML={{ __html: sanitizeHTML(recommendation.text) }}
				/>
			))}
		</>
	);


	const hasRecommendations = (
		(recommend_for_you.texts && recommend_for_you.texts.length > 0) ||
		(recommend_for_you.litres && recommend_for_you.litres.length > 0) ||
		(recommend_for_you.recommends && recommend_for_you.recommends.length > 0)
	);

	if (!hasRecommendations) {
		return null;
	}

	return (
		<Grid col='1' gap='2'>
			<ResultH2>{text_recommendations}</ResultH2>
			<StyledArticle>
				<ul>
					<ConditionalBlock block={resultRecommendText}>
						{recommend_for_you.texts && recommend_for_you.texts.length > 0 && (
							<>
								{renderText(recommend_for_you.texts)}
							</>
						)}
					</ConditionalBlock>

					<ConditionalBlock block={resultRecommendBooks}>
						{recommend_for_you.litres && recommend_for_you.litres.length > 0 && (
							<>
								{renderLitres(recommend_for_you.litres)}
							</>
						)}
					</ConditionalBlock>

					<ConditionalBlock block={resultRecommendForYou}>
						{recommend_for_you.recommends && recommend_for_you.recommends.length > 0 && (
							<>
								{renderRecommends(recommend_for_you.recommends)}
							</>
						)}
					</ConditionalBlock>
				</ul>
				{(recommend_for_you.litres && recommend_for_you.litres.length > 0 ||
					recommend_for_you.recommends && recommend_for_you.recommends.length > 0) && <p>{text_adv_info}</p>}
			</StyledArticle>
		</Grid>

	);
};

export default ResultFooterRecommendations;
